"use client";

import { useLingui } from "@lingui/react/macro";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { useTrackEvent } from "@/components/analytics/events";
import type { ReportExportedPayloadWithoutFormat } from "@/lib/analytics/custom-events";
import {
  type CaptureOptions,
  copyElementAsPng,
  downloadElementAsPng,
} from "./element";

type ElementResolver = () => HTMLElement | null;

export type UseElementCaptureOptions = {
  getElement: ElementResolver;
  filename?: string;
  captureOptions?: CaptureOptions;
  reportExportedPayload?: ReportExportedPayloadWithoutFormat;
};

export type UseElementCaptureResult = {
  copy: () => Promise<void>;
  download: () => Promise<void>;
  isCopying: boolean;
  isDownloading: boolean;
};

/**
 * キャプチャ操作を共通化するカスタムフック。
 * コピー／ダウンロード双方の状態とトースト制御をまとめて扱う。
 */
export function useElementCapture({
  getElement,
  filename,
  captureOptions,
  reportExportedPayload,
}: UseElementCaptureOptions): UseElementCaptureResult {
  const trackEvent = useTrackEvent();
  const { t } = useLingui();
  const [isCopying, setIsCopying] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const copy = useCallback(async () => {
    if (isCopying) {
      return;
    }

    const element = getElement();

    if (!element) {
      toast.error(t`Capture target could not be found.`);
      return;
    }

    setIsCopying(true);

    try {
      await copyElementAsPng(element, captureOptions);
      if (reportExportedPayload) {
        trackEvent({
          eventName: "report_exported",
          payload: { ...reportExportedPayload, export_format: "copy" },
        });
      }
      toast.success(t`Card capture image copied to clipboard.`);
    } catch (error) {
      if (error instanceof DOMException && error.name === "NotAllowedError") {
        toast.error(
          t`Clipboard access was denied. Please review your browser settings.`,
        );
      } else {
        toast.error(t`Copy failed. Please try again with a modern browser.`);
      }
    } finally {
      setIsCopying(false);
    }
  }, [
    captureOptions,
    getElement,
    isCopying,
    reportExportedPayload,
    t,
    trackEvent,
  ]);

  const download = useCallback(async () => {
    if (isDownloading) {
      return;
    }

    const element = getElement();

    if (!element) {
      toast.error(t`Capture target could not be found.`);
      return;
    }

    setIsDownloading(true);

    try {
      await downloadElementAsPng(element, {
        ...captureOptions,
        filename,
      });
      if (reportExportedPayload) {
        trackEvent({
          eventName: "report_exported",
          payload: { ...reportExportedPayload, export_format: "image" },
        });
      }
      toast.success(t`Saved the card capture image as an image file.`);
    } catch {
      toast.error(t`Saving the image failed.`);
    } finally {
      setIsDownloading(false);
    }
  }, [
    captureOptions,
    filename,
    getElement,
    isDownloading,
    reportExportedPayload,
    t,
    trackEvent,
  ]);

  return {
    copy,
    download,
    isCopying,
    isDownloading,
  };
}
